import i18next from 'i18next'
import { CHART_COLORS, getBarChartDefaults } from 'components/charts/constants'
import { createChartDataArray, formatChartDateLabel } from 'utils/chartjs'
import { capitalizeFirstLetter, formatAmount } from 'utils/format'
import { ChartOptions, TooltipItem } from 'chart.js'
import { ConsumptionBarDataset, ConsumptionChartOptions, GetConsumptionChartDatasets } from './types'
import { ChartColorKeys, ExternalTooltipContext } from 'components/charts/types.ts'
import { externalTooltipHandler } from 'components/charts/chart-tooltip/tooltip.ts'
import { TOOLTIP_CONFIG } from 'components/charts/chart-tooltip/config.ts'
import { ENERGY_TYPES } from '@boltenergy-be/shared'
import { Granularity } from 'types/contracts.ts'
import { isIntradayGranularity } from 'utils/date.ts'
import { getConsumptionEntryColor } from './utils.ts'

/**
 * Get consumption chart options
 * @param {ConsumptionChartOptions} params
 */
export const getConsumptionChartOptions = ({
  granularity,
  handleHoverDataPoint,
  isMobile,
  labels,
  minMax
}: ConsumptionChartOptions): ChartOptions<'bar'> => {
  const defaults = getBarChartDefaults({ isMobile })
  const {
    scales: { y, x }
  } = defaults

  return {
    ...defaults,
    maintainAspectRatio: false,
    skipNull: true,
    // A day holds 96 quarter hours, too many to narrow the bars any further than their category (see MarketPrices)
    ...(granularity === Granularity.QUARTER_HOUR && { datasets: { bar: { barPercentage: 1 } } }),
    // Keeps the hovered bar highlight in sync with the tooltip, which triggers on the nearest bar of the hovered column
    interaction: {
      intersect: false,
      mode: 'index'
    },
    scales: {
      y: {
        ...y,
        suggestedMin: minMax?.minDataValue,
        suggestedMax: minMax?.maxDataValue,
        stacked: true,
        ticks: {
          ...y.ticks,
          callback: (value: string | number) => formatAmount(Number(value), { maxFractionDigits: 3, minFractionDigits: 0 })
        }
      },
      x: {
        ...x,
        stacked: true,
        ticks: {
          ...x.ticks,
          callback: (value: string | number) => {
            return formatChartDateLabel(labels[Number(value)], granularity)
          }
        }
      }
    },
    plugins: {
      yScaleTitle: {
        labels: {
          y: 'kWh'
        }
      },
      tooltip: {
        ...TOOLTIP_CONFIG,
        external: (context: ExternalTooltipContext<'bar'>) => {
          const { tooltip } = context
          const currentDataPoint = tooltip?.dataPoints?.[0]?.dataIndex
          const hideTooltip = tooltip.opacity === 0
          handleHoverDataPoint(hideTooltip ? null : currentDataPoint)
          return externalTooltipHandler({ context, totalColumns: labels.length })
        },
        callbacks: {
          title: (context: TooltipItem<'bar'>[]) =>
            capitalizeFirstLetter(formatChartDateLabel(context?.[0]?.label, granularity, !isIntradayGranularity(granularity)))
        }
      }
    }
  }
}

export const getConsumptionChartDatasets = ({
  labels,
  data,
  type,
  showInjection,
  showEstimatedUsage,
  isMobile,
  hasSmartMeter
}: GetConsumptionChartDatasets): ConsumptionBarDataset[] => {
  const borderRadius = isMobile ? 4 : 2
  const maxBarThickness = 94
  const isElek = type === ENERGY_TYPES.electricity

  const barCharts: ConsumptionBarDataset[] = [
    {
      type: 'bar',
      label: i18next.t(($) => $.chart.legend.usage, {
        ns: 'consumption'
      }),
      backgroundColor: CHART_COLORS[getConsumptionEntryColor(type, 'billed') as ChartColorKeys],
      hoverBackgroundColor: CHART_COLORS[getConsumptionEntryColor(type, 'billed', true) as ChartColorKeys],
      data: createChartDataArray(
        labels?.length,
        data.map(({ consumption }) => consumption.billed || 0)
      ),
      borderSkipped: 'middle',
      maxBarThickness,
      borderRadius,
      yAxisID: 'y'
    }
  ]

  if (showEstimatedUsage && data?.some((entry) => entry.consumption.unbilled)) {
    barCharts.push({
      type: 'bar',
      label: i18next.t(($) => $.chart.legend[!hasSmartMeter ? 'estimated' : 'unbilled'], { ns: 'consumption' }),
      backgroundColor: CHART_COLORS[getConsumptionEntryColor(type, 'unbilled') as ChartColorKeys],
      hoverBackgroundColor: CHART_COLORS[getConsumptionEntryColor(type, 'unbilled', true) as ChartColorKeys],
      data: createChartDataArray(
        labels?.length,
        data.map(({ consumption }) => consumption.unbilled || 0)
      ),
      borderSkipped: 'middle',
      maxBarThickness,
      borderRadius,
      yAxisID: 'y'
    } satisfies ConsumptionBarDataset)
  }

  if (isElek && showInjection) {
    barCharts.push({
      type: 'bar',
      label: i18next.t(($) => $.chart.legend.injection, {
        ns: 'consumption'
      }),
      backgroundColor: CHART_COLORS[getConsumptionEntryColor('IN', 'billed') as ChartColorKeys],
      hoverBackgroundColor: CHART_COLORS[getConsumptionEntryColor('IN', 'billed', true) as ChartColorKeys],
      data: createChartDataArray(
        labels?.length,
        data.map(({ injection }) => injection?.billed || 0)
      ),
      borderSkipped: 'middle',
      maxBarThickness,
      borderRadius,
      yAxisID: 'y'
    } satisfies ConsumptionBarDataset)
  }

  if (isElek && showEstimatedUsage && data?.some((entry) => entry.injection?.unbilled) && showInjection) {
    barCharts.push({
      type: 'bar',
      label: i18next.t(($) => $.chart.legend[!hasSmartMeter ? 'estimated' : 'unbilled'], { ns: 'consumption' }),
      backgroundColor: CHART_COLORS[getConsumptionEntryColor('IN', 'unbilled') as ChartColorKeys],
      hoverBackgroundColor: CHART_COLORS[getConsumptionEntryColor('IN', 'unbilled', true) as ChartColorKeys],
      data: createChartDataArray(
        labels?.length,
        data.map(({ injection }) => injection?.unbilled || 0)
      ),
      borderSkipped: 'middle',
      maxBarThickness,
      borderRadius,
      yAxisID: 'y'
    } satisfies ConsumptionBarDataset)
  }

  return barCharts
}
